import { useMemo } from 'react';

import { ColumnDef } from '@tanstack/react-table';

import { useDataTable } from '@/hooks/use-data-table';
import { TableIds } from '@/lib/constants/table-ids';
import { SingleSelectOption } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { UserGroup } from '@/modules/common/types/user-group';

import { getMemberAccessColumns } from '../../constants/member-access-table-columns';
import { MemberAccessRow } from '../../types/member-access';

interface UseMemberAccessTableParams {
  /** Currently-loaded visible rows (the query window). */
  data: MemberAccessRow[];
  groupOptions: SingleSelectOption[];
  isInviteMode: boolean;
  /** The organization's default group — applied to rows invited without an explicit group. */
  defaultGroup: UserGroup | null;
}

export const useMemberAccessTable = ({
  data,
  groupOptions,
  isInviteMode,
  defaultGroup,
}: UseMemberAccessTableParams) => {
  // Columns are rebuilt only when groups or the mode change — NOT on edits.
  const columns = useMemo<ColumnDef<MemberAccessRow>[]>(
    () => getMemberAccessColumns({ groupOptions, isInviteMode, defaultGroup }),
    [groupOptions, isInviteMode, defaultGroup]
  );

  return useDataTable<MemberAccessRow>({
    data,
    columns,
    getRowId: row => String(row.userId),
    tableId: TableIds.MEMBER_ACCESS,
    enableRowSelection: false,
  });
};
